import { Box, Button, Toolbar, Typography, useTheme } from "@mui/material";
import { Column, SortingState } from "@tanstack/react-table";
import { ReactNode, useState } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useLocalization } from "../../hooks/useLocalization";
import { SearchBar } from "../SearchBar";
import { FilterModal } from "./FilterModal";
import { SortModal } from "./SortModal";

interface TableToolbarProps<T extends object> {
  setGlobalFilter: (value: string) => void;
  globalFilterValue: string;
  setGlobalFilterValue: (value: string) => void;
  globalFilterPlaceholder?: string;
  filterSlot?: ReactNode;
  onActionClick?: () => void;
  actionButtonText?: string;
  actionButtonTextShort?: string;
  actionButtonIcon?: ReactNode;
  showActionButton?: boolean;
  columns?: Column<T>[];
  facetedColumnIds?: string[];
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
}

export const TableToolbar = <T extends object>({
  setGlobalFilter,
  globalFilterValue,
  setGlobalFilterValue,
  globalFilterPlaceholder,
  filterSlot,
  onActionClick,
  actionButtonText,
  actionButtonIcon,
  showActionButton,
  columns = [],
  facetedColumnIds = [],
  sorting = [],
  onSortingChange,
  actionButtonTextShort,
}: TableToolbarProps<T>): React.ReactElement => {
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [isSortDialogOpen, setIsSortDialogOpen] = useState(false);

  const { t } = useLocalization();

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setGlobalFilter(globalFilterValue);
  };

  const handleClear = () => {
    setGlobalFilter("");
    setGlobalFilterValue("");
  };

  const shouldShow = !!showActionButton && !!onActionClick;

  const isMobile = useIsMobile();

  const actionTextToShow = isMobile ? actionButtonTextShort : actionButtonText;

  const getCurrentSortText = () => {
    if (sorting.length === 0) {
      return t("components.dataTable.none");
    }

    const currentSortItem = sorting[0];
    if (currentSortItem) {
      const sortedColumn = columns.find((col) => col.id === currentSortItem.id);
      if (sortedColumn) {
        const columnName = String(sortedColumn.columnDef.header);
        const direction = currentSortItem.desc
          ? t("components.dataTable.desc")
          : t("components.dataTable.asc");

        return `${columnName} (${direction})`;
      }
    }

    return t("components.dataTable.none");
  };

  const getCurrentFilterText = () => {
    const activeFilters: string[] = [];

    facetedColumnIds.forEach((columnId) => {
      const column = columns.find((col) => col.id === columnId);
      if (column) {
        const filterValue = column.getFilterValue();
        if (Array.isArray(filterValue) && filterValue.length > 0) {
          const columnName = String(column.columnDef.header);
          activeFilters.push(`${columnName} (${filterValue.length})`);
        }
      }
    });

    return activeFilters.length > 0
      ? activeFilters.join(", ")
      : t("components.dataTable.none");
  };

  const hasFacetedColumns = facetedColumnIds.length > 0;

  const hasSortableColumns = columns.some((column) => column.getCanSort());

  return (
    <Toolbar
      disableGutters
      sx={{
        flexWrap: "wrap",
        alignItems: "center",
        pt: { xs: 0, md: 3 },
        pb: { xs: 0, md: 2 },
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: { xs: 160, md: 240 },
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flex: 1,
            flexWrap: "wrap",
            minWidth: { xs: 160, md: 240 },
            gap: 1,
          }}
        >
          <form
            onSubmit={handleSubmit}
            style={filterSlot ? { flex: "1 1 200px" } : { flexGrow: 1 }}
          >
            <SearchBar
              handleClear={handleClear}
              handleSubmit={handleSubmit}
              onChange={(e) => setGlobalFilterValue(e.target.value)}
              placeholder={globalFilterPlaceholder}
              style={{ marginTop: "4px" }}
              value={globalFilterValue}
            />
          </form>
          {filterSlot}
          {shouldShow && (
            <Button
              color="primary"
              disabled={!showActionButton}
              onClick={onActionClick}
              startIcon={actionButtonIcon}
              sx={{ height: "40px" }}
              variant="contained"
            >
              {actionTextToShow ?? actionButtonText}
            </Button>
          )}
        </Box>

        {isMobile && (
          <Box
            sx={{
              display: "flex",
              flex: 1,
              my: 1,
            }}
          >
            {hasSortableColumns && (
              <>
                <LabeledToolbarButton
                  actionText={getCurrentSortText()}
                  label={t("components.dataTable.sortByLabel")}
                  onClick={() => setIsSortDialogOpen(true)}
                />
                <SortModal
                  columns={columns}
                  isOpen={isSortDialogOpen}
                  onClose={() => setIsSortDialogOpen(false)}
                  onSortingChange={onSortingChange}
                  sorting={sorting}
                />
              </>
            )}
            {hasFacetedColumns && (
              <>
                <LabeledToolbarButton
                  actionText={getCurrentFilterText()}
                  label={t("components.dataTable.filterLabel")}
                  onClick={() => setIsFilterDialogOpen(true)}
                />
                <FilterModal
                  columns={columns}
                  facetedColumnIds={facetedColumnIds}
                  isOpen={isFilterDialogOpen}
                  onClose={() => setIsFilterDialogOpen(false)}
                />
              </>
            )}
          </Box>
        )}
      </Box>
    </Toolbar>
  );
};

interface LabeledToolbarButtonProps {
  label: string;
  onClick: () => void;
  actionText: string;
}

const LabeledToolbarButton = ({
  label,
  onClick,
  actionText,
}: LabeledToolbarButtonProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        ml: "16px",
      }}
    >
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: 12,
          color: theme.colors.text[1],
          alignSelf: "center",
        }}
      >
        {label}
      </Typography>
      <Button
        onClick={onClick}
        sx={{
          fontWeight: 600,
          fontSize: 14,
          color: theme.colors.text[5],
          textDecoration: "underline",
        }}
      >
        {actionText}
      </Button>
    </Box>
  );
};
